import * as React from 'react';
import equal from 'fast-deep-equal/react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from '~/Reducers';
import { openBundleByUrl } from '~/utils/category';
import classNames from 'classnames';
import styles from './SantaBundle.scss';
import BundleAmount from '~/components/BundleAmount/BundleAmount';
import { selectGroupBundle, startVideo } from '~/Actions/ActionApp';
import PriceWrapper from '~/components/PriceWrapper/PriceWrapper';
import PurchaseButton, { PurchaseButtonTheme } from '~/components/Button/PurchaseButton';
import { ICurrentPage } from '~/Actions/ActionAppType';
import { DEFAULT_SELECTED_INDEX_BY_GROUP, isCertificateShowcase, SANTA_BUNDLES_AUDIO_MAP, SANTA_BUNDLE_CARD_VIDEOS, SANTA_SIZE, VIDEO_BUNDLE_PREVIEW } from '~/Layouts/Themes/SantaPage/settings';
import { getStaticUrl, isInGameBrowser, isMobileOrTabletWindow } from '~/utils/utils';
import { Howl } from 'howler';
import { useAvailableSound } from '~/hooks/useAvailableSound';
import useResourceLoader from '~/hooks/useResourceLoader';
import { getSupportedVideo, isVideoTypeSupported } from '~/utils/video';
import { groupBundlesByName } from '~/utils/bundles';
import isDisabledAnimation from '~/hooks/isDisabledAnimation';
import { UserGraphicsQualityPreset, UserGraphicsQualityPresetTypes } from '@wg/web2clientapi/browser/getUserGraphicsQualityPreset';
import preCacheImage from '~/utils/preCacheImage';
import { urlsMap } from '~/utils/menu';
import History from '~/utils/history';

interface ISantaBundle {
    groupName: string;
    groups: ReturnType<typeof groupBundlesByName>;
    selectedGroup: string;
    selectedIndex: number;
    isLastGroup: boolean;
    isDisabledAnimation?: boolean;
}

interface IStateSelector {
    currentPage: ICurrentPage;
    groupBundles: Record<string, number>;
    categoriesAnimationStatuses: ICategoriesAnimationStatuses;
    userGraphics: UserGraphicsQualityPreset;
    categories: ICategories;
}

const stateSelector = (state: State): IStateSelector => {
    return {
        currentPage: state.ReducerApp.currentPage,
        groupBundles: state.ReducerApp.groupBundles,
        categoriesAnimationStatuses: state.ReducerApp.categoriesAnimationStatuses,
        userGraphics: state.ReducerAccount.userGraphics,
        categories: state.ReducerApp.categories,
    };
};

const SantaBundle = React.forwardRef((props: ISantaBundle, ref: React.RefObject<HTMLDivElement>) => {
    const dispatch = useDispatch();
    const state = useSelector<State, IStateSelector>(stateSelector, equal);
    const selectedBundleStore = state.groupBundles || {};
    const currentGroupsBundles: IBundle[] = props.groups[props.groupName];
    const isSelected = props.selectedGroup === props.groupName;
    let _isDisabledAnimation = isDisabledAnimation(state.currentPage?.name);
    if (!isVideoTypeSupported('webm')) {
        _isDisabledAnimation = true;
    }
    const idleAnimationRef = React.useRef<HTMLVideoElement>(null);
    const hoverAnimationRef = React.useRef<HTMLVideoElement>(null);
    const SIZE = SANTA_SIZE[props.groupName];
    const howl = new Howl({ src: SANTA_BUNDLES_AUDIO_MAP[SIZE] });
    const isAvailableSoundEffect = useAvailableSound(state.categories[state.currentPage?.name], null);
    const animationsMap = SANTA_BUNDLE_CARD_VIDEOS[SIZE];

    const selectedBundleIndex = currentGroupsBundles?.reduce((state: number, bundle: IBundle, index: number) => {
        if (bundle.id === selectedBundleStore?.[props.groupName]) {
            state = index;
        }
        return state;
    }, null);

    let currentBundle: IBundle;

    if (selectedBundleIndex !== null && selectedBundleIndex !== undefined) {
        currentBundle = currentGroupsBundles[selectedBundleIndex];
    } else {
        currentBundle = currentGroupsBundles[DEFAULT_SELECTED_INDEX_BY_GROUP[props.groupName] || 0] || currentGroupsBundles[0];
    }

    const supportedIntroVideoUrl = getSupportedVideo(VIDEO_BUNDLE_PREVIEW[SIZE]?.intro);

    const [selectedBundle, updateSelectedBundle] = React.useState(currentBundle);
    const [isLoadingResources, resourcesMap] = useResourceLoader([animationsMap.idle, animationsMap.hover]);
    const [isLoadingIntroBundleVideo, resourceIntroVideo] = useResourceLoader([supportedIntroVideoUrl], isInGameBrowser);

    if (!selectedBundle) {
        return null;
    }

    const goToBundle = () => {
        const redirect = () => openBundleByUrl(state.currentPage?.name, selectedBundle.id);
        if (isMobileOrTabletWindow) {
            History.navigate(urlsMap.home, { replace: true });
        } else {
            if (isLoadingIntroBundleVideo && state.userGraphics?.preset !== UserGraphicsQualityPresetTypes.LOW) {
                dispatch(startVideo('bundle_preview', resourceIntroVideo[supportedIntroVideoUrl]));
                setTimeout(redirect, 500);
            } else {
                History.navigate(urlsMap.home, { replace: true });
            }
        }
    };

    const isShowcaseForCertificates = isCertificateShowcase(state.currentPage?.name);

    const classes = classNames(styles.bundle, styles[SANTA_SIZE[props.groupName]], props.groupName, {
        [styles.selected]: isSelected,
        [styles.fromCertificate]: isShowcaseForCertificates,
    });

    const bundle = { ...selectedBundle, quantityData: {} };

    const classesIdleVideo = classNames(styles.video, styles.animationIdle, {
        [styles.visible]: !_isDisabledAnimation && isLoadingResources,
    });

    const classesHoverVideo = classNames(styles.video, styles.animationHover);

    const classesImage = classNames(styles.image, styles[SIZE], {
        [styles.hidden]: !_isDisabledAnimation && isLoadingResources,
    });

    const classesHoverImage = classNames(styles.image, styles[SIZE], styles.hover, {
        [styles.hidden]: !_isDisabledAnimation && isLoadingResources,
    });

    const startAnimation = () => {
        const idle = idleAnimationRef.current;
        const hover = hoverAnimationRef.current;
        idle.classList.add(styles.hiddenIdleAnimation);
        hover.classList.add(styles.visibleHoverAnimation);
        hover.play();
    };

    const stopAnimation = () => {
        const idle = idleAnimationRef.current;
        const hover = hoverAnimationRef.current;
        idle.classList.remove(styles.hiddenIdleAnimation);
        hover.classList.remove(styles.visibleHoverAnimation);
        hover.pause();
        hover.currentTime = 0;
    };

    const onMouseEnter = () => {
        if (isAvailableSoundEffect && !howl.playing()) {
            howl.volume(1);
            howl.play();
        }
        !_isDisabledAnimation && !isMobileOrTabletWindow && isLoadingResources && startAnimation();
    };

    const onMouseLeave = () => {
        !_isDisabledAnimation && !isMobileOrTabletWindow && isLoadingResources && stopAnimation();
    };

    preCacheImage.add(bundle.icons?.small);

    return (
        <div className={classes} onClick={goToBundle} ref={ref} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <div className={styles.imageContainer}>
                <img className={classesImage} src={getStaticUrl(require(`./images/static_box_idle_${SIZE}.png`))} />
                <img className={classesHoverImage} src={getStaticUrl(require(`./images/static_box_hover_${SIZE}.png`))} />
                {!isMobileOrTabletWindow && (
                    <>
                        <video className={classesIdleVideo} preload={'auto'} autoPlay={true} muted loop src={resourcesMap[animationsMap.idle]} ref={idleAnimationRef} />
                        <video className={classesHoverVideo} preload={'auto'} autoPlay={false} loop muted src={resourcesMap[animationsMap.hover]} ref={hoverAnimationRef} />
                    </>
                )}
            </div>
            <div className={styles.info}>
                <div className={styles.title}>{selectedBundle.title}</div>
            </div>
            {currentGroupsBundles.length > 1 && (
                <div className={styles.amount}>
                    <BundleAmount
                        bundles={currentGroupsBundles}
                        selectedBundleId={selectedBundle.id}
                        onChange={(bundle) => {
                            updateSelectedBundle(bundle);
                            dispatch(selectGroupBundle(props.groupName, bundle.id));
                        }}
                    />
                </div>
            )}
            <div className={styles.priceWrapper}>
                <PriceWrapper
                    bundle={bundle}
                    discountClassName={styles.discount}
                    withoutAnimation={false}
                    wrapperClassName={styles.pricesWrapper}
                    priceClassName={classNames({
                        [styles.price]: !isShowcaseForCertificates,
                    })}
                />
            </div>
            <div className={styles.purchaseWrapper}>
                <div className={styles.purchase}>
                    <PurchaseButton
                        bundle={selectedBundle}
                        buttonClassName={styles.button}
                        shortageClassName={styles.shortageClassName}
                        classNameFastGoldLink={styles.fastGoldWrapper}
                        classNameChinaFastGoldLink={styles.fastGoldWrapper}
                        purchaseButtonTheme={isShowcaseForCertificates ? PurchaseButtonTheme.blue : PurchaseButtonTheme.orange}
                    />
                </div>
            </div>
        </div>
    );
});

export default SantaBundle;
